import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowDownTrayIcon, ArrowLeftIcon, ArrowUpTrayIcon, EyeIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { toast } from "../../../utils/toast";
import { Button, Card, ConfirmModal, Container, EmptyState, Loading, PageHeader, Property, RowAction, Table, Tag } from "../../ui";
import { errorMessageOf } from "../../../api/scholarships";
import { countryName, formatKigaliDate } from "../model";
import { AMOUNTS, formatMoney } from "../funding";
import { fileSummary, formatDateTime, formatLocalDateTime, fullName } from "../review/model";
import MessageThread from "../loans/MessageThread";
import { PaymentDialog, TicketDialog } from "./BrdDialogs";
import { SCHOLARSHIP_LOANS, paymentKindOf, useBrdLoan, useBrdLoans } from "./model";
import { ABROAD_LOANS } from "../../abroadLoans/awards";

// A loan in Rwanda has no travel: BRD buys no ticket and pays the institution that admitted the student
const destinationOf = (l) =>
  l.travels
    ? [l.programme.institutionName, l.programme.countryCode && countryName(l.programme.countryCode)].filter(Boolean).join(", ") || "Not given"
    : `${l.admittedInstitution ?? l.programme.institutionName}, Rwanda`;
const ticketOf = (l) => {
  if (!l.travels) return "Not needed";
  if (l.ticketIssuedAt) return "Issued";
  if (l.travelSubmittedAt && l.waitingForVisa) return "Waiting for the visa";
  return l.travelSubmittedAt ? "To buy" : "Waiting";
};

const ticketTag = (l) => {
  if (!l.travels) return <span className="text-slate-400">Not needed</span>;
  if (l.ticketIssuedAt) return <Tag color="green">Issued</Tag>;
  // The student's country of study asks for a visa they have not uploaded yet
  if (l.travelSubmittedAt && l.waitingForVisa) return <Tag color="gray">Waiting for the visa</Tag>;
  return l.travelSubmittedAt ? <Tag color="yellow">To buy</Tag> : <span className="text-slate-400">Waiting for travel details</span>;
};

// Every abroad loan a student accepted, for BRD to pay out, from the Loans service. Scholarships no
// longer carry loans, so the scholarships API has none to add.
export function LoanPaymentsPage() {
  const navigate = useNavigate();
  const abroadLoans = useBrdLoans(ABROAD_LOANS);
  const data = (abroadLoans.data ?? []).map((l) => ({ ...l, source: ABROAD_LOANS }));
  const { isPending, isError, refetch } = abroadLoans;
  const open = (l) => navigate(l.source.pathOf(l.applicationId));

  const columns = [
    { key: "applicant", label: "Student", accessor: (l) => fullName(l.applicant), render: (l) => <span className="font-medium text-slate-800">{fullName(l.applicant)}</span> },
    { key: "scholarship", label: "Scholarship", accessor: (l) => l.scholarship.name, filter: "Any scholarship" },
    { key: "destination", label: "Destination", accessor: destinationOf },
    {
      key: "travel",
      label: "Travel details",
      accessor: (l) => (!l.travels ? "Not needed" : l.travelSubmittedAt ? formatKigaliDate(l.travelSubmittedAt) : "Not sent"),
      sortValue: (l) => l.travelSubmittedAt ?? "",
    },
    { key: "ticket", label: "Ticket", accessor: ticketOf, filter: "Any ticket", render: ticketTag },
    { key: "tuition", label: "Tuition paid", accessor: (l) => formatMoney(l.tuitionPaid, l.approvedFunding.currency), sortValue: (l) => l.tuitionPaid },
    { key: "allowances", label: "Allowances sent", accessor: (l) => formatMoney(l.allowancesSent, l.approvedFunding.currency), sortValue: (l) => l.allowancesSent },
    {
      key: "messages",
      label: "Messages",
      accessor: (l) => (l.awaitingReply ? "Waiting for your reply" : l.lastMessageAt ? formatDateTime(l.lastMessageAt) : "—"),
      sortValue: (l) => l.lastMessageAt ?? "",
      render: (l) => (l.awaitingReply ? <Tag color="blue">Waiting for your reply</Tag> : l.lastMessageAt ? formatDateTime(l.lastMessageAt) : "—"),
    },
    { key: "open", label: "", exportable: false, render: (l) => <RowAction Icon={EyeIcon} label="Open" onClick={() => open(l)} /> },
  ];

  return (
    <Container className="flex flex-col gap-6 py-6">
      <PageHeader title="Loan payments" />
      {isError ? (
        <EmptyState title="Could not load the loans" onRefresh={refetch} />
      ) : (
        <Table cardView columns={columns} data={data} onRowClick={open} isLoading={isPending} exportName="loan-payments" emptyTitle="No accepted loans yet" />
      )}
    </Container>
  );
}

const TRAVEL = [
  ["departureAirport", "Departure airport"],
  ["arrivalAirport", "Arrival airport"],
  ["preferredTravelDate", "Preferred travel date"],
  ["passportNumber", "Passport number"],
  ["passportExpiry", "Passport expiry"],
];
const TRAVEL_DATES = new Set(["preferredTravelDate", "passportExpiry"]);

// One accepted loan: the student's travel details, BRD's ticket and payments, and the messages.
// `source` is where it lives (SCHOLARSHIP_LOANS or ABROAD_LOANS)
export function LoanPaymentPage({ source = SCHOLARSHIP_LOANS }) {
  const { applicationId } = useParams();
  const queryClient = useQueryClient();
  const { data: loan, isPending, isError, refetch } = useBrdLoan(applicationId, source);
  const [dialog, setDialog] = useState(null); // "ticket" or "payment"
  const [removing, setRemoving] = useState(null); // the payment to remove
  const [busy, setBusy] = useState(false);

  if (isPending) return <Loading label="Loading the loan…" />;
  if (isError) return <EmptyState title="Could not load this loan" onRefresh={refetch} />;

  const { travel, approvedFunding: funding } = loan;
  const done = (fresh) => {
    queryClient.setQueryData([...source.key, applicationId], fresh);
    queryClient.invalidateQueries({ queryKey: source.key });
    setDialog(null);
  };

  const removePayment = async () => {
    setBusy(true);
    try {
      await source.removePayment(applicationId, removing.id);
      toast.success("Payment removed");
      queryClient.invalidateQueries({ queryKey: source.key });
      setRemoving(null);
    } catch (error) {
      console.error(`Failed to remove payment ${removing.id} of application ${applicationId}:`, error);
      toast.error(errorMessageOf(error, "Failed to remove the payment. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const paymentColumns = [
    { key: "paidOn", label: "Paid on", accessor: (p) => formatKigaliDate(p.paidOn), sortValue: (p) => p.paidOn },
    { key: "kind", label: "Payment", accessor: (p) => paymentKindOf(p.kind, loan.travels), filter: "Any payment" },
    { key: "amount", label: "Amount", accessor: (p) => formatMoney(p.amount, p.currency), sortValue: (p) => p.amount },
    { key: "reference", label: "Reference", accessor: (p) => p.reference },
    { key: "details", label: "Details", accessor: (p) => [p.embassy, p.note].filter(Boolean).join(" · ") || "—" },
    { key: "recordedBy", label: "Recorded by", accessor: (p) => p.recordedBy },
    { key: "remove", label: "", exportable: false, render: (p) => <RowAction Icon={TrashIcon} label="Remove" onClick={() => setRemoving(p)} /> },
  ];

  return (
    <Container className="flex flex-col gap-6 py-6">
      <Link to="/loan-payments" className="flex w-fit items-center gap-1 text-sm font-medium text-brand hover:underline">
        <ArrowLeftIcon className="h-4 w-4" /> All loan payments
      </Link>
      <PageHeader
        title={fullName(loan.applicant)}
        subtitle={`${loan.scholarship.name} · ${loan.programme.name}, ${destinationOf(loan)}`}
        actions={
          source.openLetter && (
            <Button variant="outline" size="sm" icon={<ArrowDownTrayIcon className="h-4 w-4" />} onClick={() => source.openLetter(applicationId)}>
              Approval letter
            </Button>
          )
        }
      />

      <div className="grid gap-6 lg:grid-cols-2">
        <Card title="Approved loan">
          {AMOUNTS.filter(({ key }) => funding[key] != null).map(({ key, label }) => (
            <Property key={key} label={label} value={formatMoney(funding[key], funding.currency)} />
          ))}
          <Property label="Tuition paid" value={formatMoney(loan.tuitionPaid, funding.currency)} />
          <Property label="Allowances sent" value={formatMoney(loan.allowancesSent, funding.currency)} />
          <Property label="Email" value={loan.applicant.email} />
          <Property label="Phone" value={loan.applicant.phoneNumber} />
        </Card>

        {travel && (
          <Card title="Travel details" actions={travel.submittedAt ? <Tag color="green">Sent {formatDateTime(travel.submittedAt)}</Tag> : <Tag color="gray">Not sent yet</Tag>}>
            {TRAVEL.map(([key, label]) => (
              <Property key={key} label={label} value={TRAVEL_DATES.has(key) && travel[key] ? formatKigaliDate(travel[key]) : travel[key]} />
            ))}
            <Property
              label="Visa"
              value={
                travel.visa ? (
                  <button type="button" className="text-brand hover:underline" onClick={() => source.openVisa(applicationId)}>
                    Open visa ({fileSummary(travel.visa)})
                  </button>
                ) : travel.visaRequired === false ? (
                  "Not needed with a Rwandan passport"
                ) : travel.visaRequired ? (
                  <span className="text-amber-700">Needed, not uploaded yet</span>
                ) : null
              }
            />
          </Card>
        )}
      </div>

      {travel && (
        <Card
          title="Ticket"
          actions={
            <Button size="sm" variant="outline" icon={<ArrowUpTrayIcon className="h-4 w-4" />} onClick={() => setDialog("ticket")} disabled={!travel.submittedAt || loan.waitingForVisa}>
              {travel.ticket ? "Replace ticket" : "Upload ticket"}
            </Button>
          }
        >
          {travel.ticket ? (
            <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-slate-700">
              <span>
                <span className="font-medium text-slate-800">
                  {travel.ticket.airline} {travel.ticket.flightNumber}
                </span>{" "}
                · departs {formatLocalDateTime(travel.ticket.departureAt)} · uploaded {formatDateTime(travel.ticket.issuedAt)}
              </span>
              <Button size="sm" variant="ghost" icon={<ArrowDownTrayIcon className="h-4 w-4" />} onClick={() => source.openTicket(applicationId)}>
                Download ticket
              </Button>
            </div>
          ) : (
            <p className="text-sm text-slate-500">
              {!travel.submittedAt
                ? "The student has not sent their travel details yet."
                : loan.waitingForVisa
                  ? "The student needs a visa for their country of study: buy the ticket once they upload it."
                  : "No ticket yet."}
            </p>
          )}
        </Card>
      )}

      <Card
        title="Payments"
        actions={
          <Button size="sm" icon={<PlusIcon className="h-4 w-4" />} onClick={() => setDialog("payment")}>
            Record payment
          </Button>
        }
      >
        <Table columns={paymentColumns} data={loan.payments} searchable={false} exportName={`payments-${fullName(loan.applicant)}`} emptyTitle="No payments yet" />
      </Card>

      <Card title="Messages">
        <MessageThread {...source.messages(applicationId)} side="BRD" />
      </Card>

      {dialog === "ticket" && <TicketDialog loan={loan} source={source} onClose={() => setDialog(null)} onDone={done} />}
      {dialog === "payment" && <PaymentDialog loan={loan} source={source} onClose={() => setDialog(null)} onDone={done} />}
      <ConfirmModal
        open={Boolean(removing)}
        onClose={() => setRemoving(null)}
        onConfirm={removePayment}
        loading={busy}
        destructive
        title="Remove this payment?"
        description={removing ? `${paymentKindOf(removing.kind, loan.travels)}: ${formatMoney(removing.amount, removing.currency)} (${removing.reference}). The application's history keeps a record of it.` : null}
        confirmLabel="Remove"
      />
    </Container>
  );
}
